/**
 * XP & level curve (gamification)
 * ───────────────────────────────
 * Pure math — no store imports (streak-store depends on this, never the
 * reverse). XP is earned only by daily check-ins: showing up is the
 * behavior worth rewarding, so the curve is flat per check-in rather than
 * scaled by streak length (streaks already have their own rewards).
 *
 * Level is ALWAYS derived from xpTotal via levelFromXp() — never stored —
 * so XP and level can never drift apart.
 *
 * Curve: going from level N to N+1 costs 50 + 20·(N−1) XP.
 * Cumulative XP required to reach level L: xpForLevel(L) = 10·(L−1)·(L+3)
 *   L1=0, L2=50, L3=120, L4=210, L5=320 …
 * A year of daily check-ins (~3,800 XP) lands around level 18.
 */

export const XP_PER_CHECKIN = 10;
export const XP_MILESTONE_BONUS = 25;

/** Cumulative XP required to reach level L (L1 = 0). */
export function xpForLevel(level: number): number {
  if (level <= 1) return 0;
  return 10 * (level - 1) * (level + 3);
}

/** XP needed to go from level N to N+1. */
export function levelCost(level: number): number {
  return 50 + 20 * (level - 1);
}

/** Current level for a given XP total. */
export function levelFromXp(xp: number): number {
  let level = 1;
  while (xpForLevel(level + 1) <= xp) level++;
  return level;
}

export interface LevelProgress {
  level: number;
  /** XP earned within the current level */
  intoLevel: number;
  /** XP needed to complete the current level */
  needed: number;
  /** 0–1 fill fraction for progress bars */
  pct: number;
}

export function levelProgress(xp: number): LevelProgress {
  const level = levelFromXp(xp);
  const intoLevel = xp - xpForLevel(level);
  const needed = levelCost(level);
  return { level, intoLevel, needed, pct: Math.min(1, intoLevel / needed) };
}
